// Google Workspace 監査実行 API クライアント。
// POST /gw/audit/run を呼び出してバックエンドから最新診断結果を取得する。
// ApiClient(frontend/src/api/client.ts) に乗せることで認証・エラー正規化を委譲する。
// toFindings は純粋関数 — API レスポンスをフロントエンドの Finding[] / GwCheck[] に変換する。
// SF audit-api.ts のミラー（persist/orgId/instanceUrl を除き domain を追加）。
import type { ApiClient } from "../api/client";
import { CHECKS, type GwCheck, type Finding, type FindingStatus } from "./audit-data";

export interface AuditApiDeps {
  readonly apiClient: ApiClient;
}

// バックエンド /gw/audit/run のレスポンスの検出単位。
export interface RunGwAuditFinding {
  readonly checkId: string;
  readonly status: "pass" | "fail" | "na" | "review";
  readonly affectedCount: number;
  readonly affectedResources: string[];
}

// バックエンド /gw/audit/run のレスポンス型（GW-SP2 backend）。
// runId は persist=true で履歴に保存されたときのみ返る（GW-SP3a）。
export interface RunGwAuditResponse {
  readonly healthCheckScore: number;
  readonly findings: RunGwAuditFinding[];
  readonly coverage: {
    readonly evaluated: string[];
    readonly notEvaluated: string[];
  };
  readonly runAt: string;
  readonly domain: string;
  readonly runId?: string;
}

// runGwAudit のオプション。persist=true の時のみ履歴に永続化される（backend は body で分岐）。
// 省略 / false は read-only（診断結果は返るがランは保存されない）。表示用呼び出しは read-only に保つ（ラン量産防止）。
export interface RunGwAuditOptions {
  readonly persist?: boolean;
}

// POST /gw/audit/run → RunGwAuditResponse
// opts.persist === true の時だけボディに { persist: true } を送り履歴へ保存させる。
export async function runGwAudit(deps: AuditApiDeps, opts?: RunGwAuditOptions): Promise<RunGwAuditResponse> {
  return deps.apiClient.post<RunGwAuditResponse>("/gw/audit/run", opts?.persist === true ? { persist: true } : {});
}

// 純粋ヘルパー: API レスポンスをフロントエンドの Finding[] と未評価 GwCheck[] に変換する。
// checkId が CHECKS カタログに存在しない場合は無視（防御的スキップ）。
export function toFindings(resp: RunGwAuditResponse): { findings: Finding[]; notEvaluated: GwCheck[] } {
  const byId = new Map<string, GwCheck>(CHECKS.map((c) => [c.id, c]));
  const findings: Finding[] = [];
  for (const rf of resp.findings) {
    const check = byId.get(rf.checkId);
    if (check === undefined) continue; // 未知 checkId は防御的スキップ
    findings.push({ check, status: rf.status as FindingStatus, affectedCount: rf.affectedCount, affectedResources: rf.affectedResources, lastDetected: resp.runAt });
  }
  const notEvaluated: GwCheck[] = [];
  for (const id of resp.coverage.notEvaluated) {
    const c = byId.get(id);
    if (c !== undefined) notEvaluated.push(c);
  }
  return { findings, notEvaluated };
}
